import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Card,
    Typography,
    Modal,
    Form,
    Input,
    message,
    Tag,
    Tooltip,
    Popconfirm,
    Switch,
    Select,
    Row,
    Col,
    Statistic,
    Spin
} from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    AppstoreOutlined,
    ShoppingOutlined,
    TagsOutlined,
    SearchOutlined
} from '@ant-design/icons';
import auctionService from '../../services/auctionService';
import DashboardLayout from '../Dashboard/DashboardLayout';
import PageHeader from '../Common/PageHeader';
import './CategoryManagement.css';

const { TextArea } = Input;

const CategoryManagement = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [form] = Form.useForm();

    const emojiOptions = [
        // Real Estate
        { value: '🏠', label: '🏠 House' },
        { value: '🏡', label: '🏡 Villa' },
        { value: '🏢', label: '🏢 Office Building' },
        { value: '🏣', label: '🏣 Post Office' },
        { value: '🏤', label: '🏤 Bank' },
        { value: '🏥', label: '🏥 Hospital' },
        { value: '🏦', label: '🏦 Bank' },
        { value: '🏨', label: '🏨 Hotel' },
        { value: '🏪', label: '🏪 Convenience Store' },
        { value: '🏫', label: '🏫 School' },
        { value: '🏬', label: '🏬 Department Store' },
        { value: '🏭', label: '🏭 Factory' },
        { value: '🏰', label: '🏰 Castle' },
        { value: '💒', label: '💒 Wedding Venue' },
        { value: '⛪', label: '⛪ Church' },
        { value: '🕌', label: '🕌 Mosque' },
        { value: '🕍', label: '🕍 Synagogue' },
        { value: '⛩️', label: '⛩️ Shrine' },
        { value: '🏛️', label: '🏛️ Classical Building' },
        { value: '🗽', label: '🗽 Statue of Liberty' },
        { value: '🗼', label: '🗼 Tokyo Tower' },
        { value: '🏟️', label: '🏟️ Stadium' },
        { value: '🎡', label: '🎡 Ferris Wheel' },
        { value: '🎢', label: '🎢 Roller Coaster' },
        { value: '🎪', label: '🎪 Circus Tent' },
        { value: '⛲', label: '⛲ Fountain' },
        { value: '🌇', label: '🌇 City Sunset' },
        { value: '🌆', label: '🌆 City Dusk' },
        { value: '🏙️', label: '🏙️ Cityscape' },
        { value: '🌃', label: '🌃 Night City' },

        // Farm Animals
        { value: '🐄', label: '🐄 Cow' },
        { value: '🐂', label: '🐂 Bull' },
        { value: '🐃', label: '🐃 Water Buffalo' },
        { value: '🐑', label: '🐑 Sheep' },
        { value: '🐐', label: '🐐 Goat' },
        { value: '🐖', label: '🐖 Pig' },
        { value: '🐓', label: '🐓 Rooster' },
        { value: '🐔', label: '🐔 Chicken' },
        { value: '🦆', label: '🦆 Duck' },
        { value: '🦃', label: '🦃 Turkey' },
        { value: '🦢', label: '🦢 Goose' },
        { value: '🐎', label: '🐎 Horse' },
        { value: '🦙', label: '🦙 Llama' },
        { value: '🦒', label: '🦒 Giraffe' },
        { value: '🦘', label: '🦘 Kangaroo' },

        // Pets
        { value: '🐕', label: '🐕 Dog' },
        { value: '🐩', label: '🐩 Poodle' },
        { value: '🐕‍🦺', label: '🐕‍🦺 Service Dog' },
        { value: '🐈', label: '🐈 Cat' },
        { value: '🐈‍⬛', label: '🐈‍⬛ Black Cat' },
        { value: '🐇', label: '🐇 Rabbit' },
        { value: '🐠', label: '🐠 Fish' },
        { value: '🐟', label: '🐟 Tropical Fish' },
        { value: '🦎', label: '🦎 Reptile' },
        { value: '🐢', label: '🐢 Turtle' },
        { value: '🐍', label: '🐍 Snake' },
        { value: '🦋', label: '🦋 Butterfly' },
        { value: '🐝', label: '🐝 Bee' },

        // Wild Animals
        { value: '🦁', label: '🦁 Lion' },
        { value: '🐯', label: '🐯 Tiger' },
        { value: '🐼', label: '🐼 Panda' },
        { value: '🦊', label: '🦊 Fox' },
        { value: '🐺', label: '🐺 Wolf' },
        { value: '🦝', label: '🦝 Raccoon' },
        { value: '🦨', label: '🦨 Skunk' },
        { value: '🦡', label: '🦡 Badger' },
        { value: '🦫', label: '🦫 Beaver' },
        { value: '🦦', label: '🦦 Otter' },

        // Birds
        { value: '🦅', label: '🦅 Eagle' },
        { value: '🦉', label: '🦉 Owl' },
        { value: '🦢', label: '🦢 Swan' },
        { value: '🦚', label: '🦚 Peacock' },
        { value: '🦃', label: '🦃 Turkey' },
        { value: '🦜', label: '🦜 Bird' },

        // Vehicles & Transportation
        { value: '🚗', label: '🚗 Car' },
        { value: '🚙', label: '🚙 SUV' },
        { value: '🏎️', label: '🏎️ Sports Car' },
        { value: '🚐', label: '🚐 Van' },
        { value: '🚚', label: '🚚 Truck' },
        { value: '🚢', label: '🚢 Boat' },
        { value: '⛵', label: '⛵ Sailboat' },
        { value: '🛥️', label: '🛥️ Motorboat' },
        { value: '✈️', label: '✈️ Aircraft' },
        { value: '🚁', label: '🚁 Helicopter' },
        { value: '🏍️', label: '🏍️ Motorcycle' },
        { value: '🛵', label: '🛵 Scooter' },
        { value: '🚲', label: '🚲 Bicycle' },

        // Luxury & Fashion
        { value: '💎', label: '💎 Jewelry' },
        { value: '💍', label: '💍 Ring' },
        { value: '👑', label: '👑 Crown' },
        { value: '⌚', label: '⌚ Watch' },
        { value: '👔', label: '👔 Business Attire' },
        { value: '👗', label: '👗 Dress' },
        { value: '👕', label: '👕 T-Shirt' },
        { value: '👖', label: '👖 Jeans' },
        { value: '👟', label: '👟 Sneakers' },
        { value: '👞', label: '👞 Dress Shoes' },
        { value: '👜', label: '👜 Handbag' },
        { value: '👛', label: '👛 Purse' },
        { value: '🕶️', label: '🕶️ Sunglasses' },

        // Electronics & Gadgets
        { value: '📱', label: '📱 Smartphone' },
        { value: '💻', label: '💻 Laptop' },
        { value: '🖥️', label: '🖥️ Desktop' },
        { value: '⌨️', label: '⌨️ Keyboard' },
        { value: '🖱️', label: '🖱️ Mouse' },
        { value: '📷', label: '📷 Camera' },
        { value: '🎥', label: '🎥 Video Camera' },
        { value: '📺', label: '📺 Television' },
        { value: '🎮', label: '🎮 Gaming Console' },
        { value: '🎲', label: '🎲 Board Games' },
        { value: '🎯', label: '🎯 Gaming Accessories' },

        // Art & Collectibles
        { value: '🎨', label: '🎨 Art' },
        { value: '🖼️', label: '🖼️ Painting' },
        { value: '🗿', label: '🗿 Sculpture' },
        { value: '🏺', label: '🏺 Pottery' },
        { value: '📚', label: '📚 Books' },
        { value: '📖', label: '📖 Rare Books' },
        { value: '🎭', label: '🎭 Theater Props' },
        { value: '🎪', label: '🎪 Circus Items' },
        { value: '🎟️', label: '🎟️ Tickets' },
        { value: '🎫', label: '🎫 Collectible Tickets' },

        // Sports & Recreation
        { value: '⚽', label: '⚽ Soccer' },
        { value: '🏀', label: '🏀 Basketball' },
        { value: '🏈', label: '🏈 Football' },
        { value: '⚾', label: '⚾ Baseball' },
        { value: '🎾', label: '🎾 Tennis' },
        { value: '🏸', label: '🏸 Badminton' },
        { value: '🎱', label: '🎱 Pool' },
        { value: '🎳', label: '🎳 Bowling' },
        { value: '🏋️', label: '🏋️ Weightlifting' },
        { value: '🚴', label: '🚴 Cycling' },

        // Music & Entertainment
        { value: '🎵', label: '🎵 Music' },
        { value: '🎸', label: '🎸 Guitar' },
        { value: '🎹', label: '🎹 Piano' },
        { value: '🎺', label: '🎺 Trumpet' },
        { value: '🎻', label: '🎻 Violin' },
        { value: '🎼', label: '🎼 Sheet Music' },
        { value: '🎧', label: '🎧 Headphones' },
        { value: '🎤', label: '🎤 Microphone' },
        { value: '🎬', label: '🎬 Film' },
        { value: '🎭', label: '🎭 Theater' },

        // Food & Beverages
        { value: '🍷', label: '🍷 Wine' },
        { value: '🥃', label: '🥃 Whiskey' },
        { value: '🍺', label: '🍺 Beer' },
        { value: '🍸', label: '🍸 Cocktail' },
        { value: '🍽️', label: '🍽️ Fine Dining' },
        { value: '🍴', label: '🍴 Silverware' },
        { value: '☕', label: '☕ Coffee' },
        { value: '🍵', label: '🍵 Tea' },
        { value: '🍫', label: '🍫 Chocolate' },
        { value: '🍪', label: '🍪 Cookies' },

        // Antiques & Vintage
        { value: '⏰', label: '⏰ Vintage Clock' },
        { value: '📞', label: '📞 Vintage Phone' },
        { value: '💼', label: '💼 Briefcase' },
        { value: '🕰️', label: '🕰️ Antique Clock' },
        { value: '🪑', label: '🪑 Furniture' },
        { value: '🛋️', label: '🛋️ Sofa' },
        { value: '🛏️', label: '🛏️ Bed' },
        { value: '🪞', label: '🪞 Mirror' },
        { value: '🖼️', label: '🖼️ Picture Frame' },
        { value: '🪟', label: '🪟 Window' },

        { value: '⚙️', label: '⚙️ Gear' },
        { value: '🔧', label: '🔧 Wrench' },
        { value: '🧰', label: '🧰 Toolbox' },
        { value: '🛠️', label: '🛠️ Tools' },
        { value: '🧪', label: '🧪 Lab Equipment' },
        { value: '🏗️', label: '🏗️ Construction Equipment' },
        { value: '🌱', label: '🌱 Seeds' },
        { value: '🪴', label: '🪴 Plants' },
        { value: '🌳', label: '🌳 Trees' },
        { value: '🌍', label: '🌍 Environment' },

        // Miscellaneous
        { value: '🎁', label: '🎁 Gift' },
        { value: '🎀', label: '🎀 Ribbon' },
        { value: '🎈', label: '🎈 Balloon' },
        { value: '🎊', label: '🎊 Celebration' },
        { value: '🎉', label: '🎉 Party' },
        { value: '🏆', label: '🏆 Trophy' },
        { value: '🎖️', label: '🎖️ Medal' },
        { value: '📜', label: '📜 Document' },
        { value: '🔑', label: '🔑 Key' },
        { value: '💡', label: '💡 Light' }
    ];

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getCategoriesWithCounts();
            setCategories(response.data);
        } catch (error) {
            console.error('Error loading categories:', error);
            message.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateEdit = async (values) => {
        try {
            setLoading(true);
            if (editingCategory) {
                await auctionService.updateCategory(editingCategory.id, values);
                message.success('Category updated successfully');
            } else {
                await auctionService.createCategory(values);
                message.success('Category created successfully');
            }
            setModalVisible(false);
            form.resetFields();
            setEditingCategory(null);
            loadCategories();
        } catch (error) {
            console.error('Error saving category:', error);
            message.error(error.response?.data?.message || 'Failed to save category');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await auctionService.deleteCategory(id);
            message.success('Category deleted successfully');
            loadCategories();
        } catch (error) {
            console.error('Error deleting category:', error);
            message.error(error.response?.data?.message || 'Failed to delete category');
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true
        },
        {
            title: 'Icon',
            dataIndex: 'icon',
            key: 'icon',
            render: (icon) => icon || '-'
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            )
        },
        {
            title: 'Auctions',
            dataIndex: 'auctions_count',
            key: 'auctions_count',
            render: (count) => count || 0
        },
        {
            title: 'Total Lots',
            dataIndex: 'total_lots',
            key: 'total_lots',
            render: (count) => count || 0
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Category">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => {
                                setEditingCategory(record);
                                form.setFieldsValue(record);
                                setModalVisible(true);
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Delete Category">
                        <Popconfirm
                            title="Are you sure you want to delete this category?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                disabled={record.auctions_count > 0}
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <DashboardLayout>
            <PageHeader
                title="Category Management"
                description="Manage auction categories and their settings"
                buttonText="Add Category"
                buttonIcon={<PlusOutlined />}
                onButtonClick={() => {
                            setEditingCategory(null);
                            form.resetFields();
                            setModalVisible(true);
                        }}
            />

            <div style={{ marginBottom: 16 }}>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                        <Card>
                            <Spin spinning={loading}>
                                <Statistic
                                    title="Total Categories"
                                    value={categories.length}
                                    prefix={<AppstoreOutlined />}
                                />
                            </Spin>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Card>
                            <Spin spinning={loading}>
                                <Statistic
                                    title="Total Auctions"
                                    value={categories.reduce((sum, cat) => sum + (cat.auctions_count || 0), 0)}
                                    prefix={<ShoppingOutlined />}
                                />
                            </Spin>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Card>
                            <Spin spinning={loading}>
                                <Statistic
                                    title="Total Lots"
                                    value={categories.reduce((sum, cat) => sum + (cat.total_lots || 0), 0)}
                                    prefix={<TagsOutlined />}
                                />
                            </Spin>
                        </Card>
                    </Col>
                </Row>

                <Card style={{ marginTop: 16, marginBottom: 16 }}>
                    <Input
                        placeholder="Search categories by name, description, or icon..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                        size="large"
                        style={{ marginBottom: 16 }}
                    />
                </Card>

                <div style={{ marginTop: 16 }}>
                        <Spin spinning={loading}>
                            <Table
                                columns={columns}
                                dataSource={categories.filter(category => {
                                    if (!searchText.trim()) return true;
                                    const search = searchText.toLowerCase();
                                    return (
                                        category.name?.toLowerCase().includes(search) ||
                                        category.description?.toLowerCase().includes(search) ||
                                        category.icon?.includes(search)
                                    );
                                })}
                                scroll={{ x: 1000 }}
                                rowKey="id"
                                pagination={{
                                    pageSize: 10,
                                    showTotal: (total) => `Total ${total} categories`
                                }}
                            />
                        </Spin>
                </div>
            </div>

            <Modal
                title={editingCategory ? 'Edit Category' : 'Create Category'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingCategory(null);
                    form.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateEdit}
                >
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Please enter category name' }]}
                    >
                        <Input placeholder="Enter category name" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea rows={4} placeholder="Enter category description" />
                    </Form.Item>

                    <Form.Item
                        name="icon"
                        label="Icon"
                    >
                        <Select
                            showSearch
                            placeholder="Select an emoji icon"
                            options={emojiOptions}
                            optionFilterProp="label"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="is_active"
                        label="Status"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {editingCategory ? 'Update' : 'Create'}
                            </Button>
                            <Button onClick={() => {
                                setModalVisible(false);
                                setEditingCategory(null);
                                form.resetFields();
                            }}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </DashboardLayout>
    );
};

export default CategoryManagement; 